import React, { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Row, Col, Collapse, Button } from 'reactstrap';
import Head from 'next/head';
import Layout from 'components/Layout';
import PageTitle from 'components/PageTitle';
import CategoriesList from 'components/Categories';
import Breadcrumb from 'components/Breadcrumb';
import { ProductsListCompare } from 'components/ProductsList';
import CompareContainer from 'components/CompareContainer';
import Paginate from 'components/Paginate';
import Filters from 'components/Filters';
import Toggler from 'components/Toggler';
import ThreeBounce from 'components/ThreeBounce';
import { fetchProducts } from 'actions/products';
import { fetchCategories } from 'actions/categories';
import { fetchRetailers } from 'actions/retailers';
import { addQuery, removeQuery } from 'lib/query-params';
import slugify from 'lib/slugify';
import { stringCompare, isDevice } from 'lib/view-helpers';
import i18n from 'i18n';
import { PAGE_SIZE, PRODUCTS_SORT_OPTIONS } from '../constants';
import { CANONICAL_URL } from 'config';

class Products extends Component {
  static async getInitialProps({
    store,
    req,
    lang,
    query: { slug, page = 1, retailer_id, sort_by }
  }) {
    const [usage_id] = slug.split('-');
    // necessary to find the category/sub-category
    store.dispatch(fetchCategories({ lang }));
    store.dispatch(fetchRetailers());
    const params = { usage_id, page, retailer_id, per_page: PAGE_SIZE };
    if (PRODUCTS_SORT_OPTIONS[sort_by]) {
      params.sort = PRODUCTS_SORT_OPTIONS[sort_by];
    } else {
      params.sort = PRODUCTS_SORT_OPTIONS.most_sustainable;
    }
    const result = store.dispatch(fetchProducts(params));
    return { usage_id, ...result };
  }

  render() {
    // @todo - this transformation and logic should be in a reducer
    // and the props should be passed directly
    const {
      products: { items, total, comparisons, loading },
      categories,
      retailers,
      usage_id,
      ...rest
    } = this.props;
    const { page, retailer_id, sort_by } = this.props.url.query;
    const category = getCategory(categories.items, usage_id);
    const parents =
      (category && getCategoryParents(category, categories.items)) || [];
    const tree = parents
      .filter((f) => f)
      .concat(category ? [category] : [])
      .map((item) => ({
        name: item.name,
        href: `/categories/${slugify(item.id, item.name)}`,
        active: item.id === category.id
      }));
    tree.unshift({ name: i18n.t('CATEGORIES'), href: '/categories' });

    const meta = { title: category ? category.name : '' };
    const _slug = category && slugify(category.id, category.name);

    return (
      <Layout {...rest} dept="product-checker" meta={meta}>
        {category && (
          <Head>
            {CANONICAL_URL && (
              <link
                rel="canonical"
                href={`${CANONICAL_URL}}/categories/${_slug}`}
              />
            )}
          </Head>
        )}
        <div className="container">
          <PageTitle center>{meta.title}</PageTitle>
          <Breadcrumb items={tree} query={{ retailer_id, sort_by }} />
          <CompareContainer comparisons={comparisons} dispatch={rest.dispatch}>
            <Row>
              <Col xs={12} sm={12} md={4} lg={3}>
                {category && (
                  <Toggler
                    initiallyToggled
                    onMount={(toggle) => isDevice('tablet') && toggle()}>
                    {(toggle, open) => (
                      <div>
                        <SidebarHeading
                          first
                          className="d-flex justify-content-between align-items-center border">
                          {i18n.t('SUB_CATEGORIES')} ({category.children.length}
                          )
                          <Button
                            color="primary-outline d-block d-lg-none"
                            onClick={toggle}
                            size="sm">
                            <span>
                              {!open && (
                                <span>
                                  <i className="fa fa-chevron-down" />
                                </span>
                              )}
                              {open && (
                                <span>
                                  <i className="fa fa-chevron-up" />
                                </span>
                              )}
                            </span>
                          </Button>
                        </SidebarHeading>
                        <Collapse isOpen={open}>
                          <CategoriesList.Sub
                            category={category}
                            query={{ retailer_id, sort_by }}
                          />
                        </Collapse>
                      </div>
                    )}
                  </Toggler>
                )}
              </Col>
              <Col xs={12} sm={12} md={8} lg={9} className="px-sm-3 px-0">
                <div className="d-flex px-sm-0 pl-2">
                  <Filters.Retailers
                    options={retailers.items}
                    selected={retailer_id}
                    onSelect={onRetailerSelect}
                  />
                  <div className="ml-2 ml-sm-3" />
                  <Filters.Sort
                    options={[
                      'most_sustainable',
                      'most_healthy',
                      'least_healthy'
                    ]}
                    selected={sort_by || 'most_sustainable'}
                    onSelect={onFilterChange}
                  />
                </div>
                <ProductsListCompare
                  items={items}
                  comparisons={comparisons}
                  dispatch={rest.dispatch}
                />
                {loading && <ThreeBounce className="w-100 mx-auto py-5" />}
                <div className="my-5 d-flex justify-content-center">
                  <Paginate current={parseInt(page) || 1} total={total} />
                </div>
              </Col>
            </Row>
          </CompareContainer>
        </div>
      </Layout>
    );
  }
}

Products.propTypes = {
  products: PropTypes.object,
  categories: PropTypes.object,
  retailers: PropTypes.object,
  url: PropTypes.object,
  usage_id: PropTypes.string
};

export default connect(({ products, categories, retailers }) => ({
  products,
  categories,
  retailers
}))(Products);

function onRetailerSelect(item) {
  if (!item.id) return removeQuery('retailer_id', 'page');
  addQuery({ retailer_id: item.id, page: 1 });
}

function onFilterChange(item) {
  addQuery({ sort_by: item.id, page: 1 });
}

export const SidebarHeading = styled.h6`
  font-weight: 400;
  color: #666;
  margin-top: ${(props) => (props.first ? 15 : 30)}px;
  margin-bottom: 15px;
  background: #f8f8f8;
  padding: 10px;
  border-radius: 4px;
`;

function getCategory(categories, categoryId) {
  const findCategory = (res, category) => {
    if (res) return res;
    if (stringCompare(category.id, categoryId)) return category;
    if (category.children && !res) {
      return category.children.reduce(findCategory, null);
    }
    return res;
  };
  return categories.reduce(findCategory, null);
}

function getCategoryParents({ id }, categories) {
  const tree = mapTree(categories);
  let category;
  const recursive = function (item) {
    item.children.map(function (child) {
      child.parents = [].concat(item.parents || []);
      child.parents.push(item.id);
      return recursive(child);
    });
    if (stringCompare(item.id, id)) category = item;
    return item;
  };
  tree.forEach(recursive);

  if (!category) return [];
  return (category.parents || []).map((id) => getCategory(categories, id));
}

function mapTree(categories) {
  const recursive = (item) => ({
    id: item.id,
    children: item.children.map(recursive),
    parents: item.parents
  });
  return categories.map(recursive);
}
